import { useState } from 'react'
import type { Moment } from 'moment-timezone'

import { splitDateTime, type ShootingDateTimeParts } from './shootingDateTime'

/**
 * The day, hour and minute the scheduling UI holds, seeded from the meeting the draft carries. Picking
 * another day clears the time, so it is chosen again against that day's availability.
 *
 * The shoot's timezone arrives after the first render. Until the client picks a value, the parts are
 * read from the meeting in whichever zone is known now, so a resumed time read in the browser's zone is
 * never recomposed in the shoot's — which moved the meeting by the difference between the two.
 */
export const useScheduleParts = (chosenDate: string | undefined, timezone: string | undefined) => {
  const [picked, setPicked] = useState<ShootingDateTimeParts | null>(null)
  const parts = picked ?? splitDateTime(chosenDate, timezone)

  const pick = (next: Partial<ShootingDateTimeParts>) => setPicked((current) => ({ ...(current ?? parts), ...next }))

  return {
    parts,
    setDay: (day: Moment | null) => {
      const sameDay = !!day && !!parts.day && day.isSame(parts.day, 'day')
      pick(sameDay ? { day } : { day, hour: undefined, minute: undefined })
    },
    setHour: (hour: number | undefined) => pick({ hour }),
    setMinute: (minute: number | undefined) => pick({ minute }),
  }
}
